/**
 * Kakuat — app del trabajador.
 *
 * PWA offline-first. El shell es deliberadamente pobre: cabecera, contenido y
 * cuatro pestañas. Todo el peso visual se lo lleva el botón de fichar, que es
 * lo que se usa el 95% del tiempo.
 *
 * El permiso de ubicación se pide DESPUÉS del login y explicado (art. 5.1.c
 * RGPD: quien cede su GPS al empleador merece saber qué se guarda). Solo una
 * vez; si dice que no, se ficha igual sin ubicación.
 */
import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { registerSW } from 'virtual:pwa-register';
import { useAppStore } from './store/useAppStore';
import { syncService } from './services/syncService';
import { getStoredTokens, getCurrentUser } from './services/apiClient';
import { getDiscardedEvents, PunchEvent } from './db/database';
import { PunchScreen } from './components/PunchScreen';
import { LoginPage } from './components/LoginPage';
import { RegistroPage } from './components/auth/RegistroPage';
import { RevisaPage } from './components/auth/RevisaPage';
import { VerificarPage } from './components/auth/VerificarPage';
import { RecuperarPage } from './components/auth/RecuperarPage';
import { RestablecerPage } from './components/auth/RestablecerPage';
import { RecuperarAccesoPage } from './components/auth/RecuperarAccesoPage';
import { WelcomeWizard } from './components/onboarding/WelcomeWizard';
import { TrialBanner } from './components/ui/TrialBanner';
import { KioskPage } from './components/KioskPage';
import { LocationPermission } from './components/LocationPermission';
import { DocumentStatus } from './components/DocumentStatus';
import { MyPlanning } from './components/MyPlanning';
import { VacationsPage } from './components/VacationsPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import { NotFound } from './pages/NotFound';
import { Icon, IconName } from './components/ui/Icon';
import { ThemeToggle } from './components/ui/ThemeToggle';

// Admin (back-office)
import { TimeCorrectionInbox } from './components/admin/TimeCorrectionInbox';
import { PendingPunchInbox } from './components/admin/PendingPunchInbox';
import { DocumentValidator } from './components/admin/DocumentValidator';
import { ResourcePlanner } from './components/admin/ResourcePlanner';
import { VacationApprover } from './components/admin/VacationApprover';
import { RotationManager } from './components/admin/RotationManager';
import { AnomalyInbox } from './components/admin/AnomalyInbox';
import { AdminAuditLog } from './components/admin/AdminAuditLog';
import { WorkersAdmin } from './components/admin/WorkersAdmin';
import { PolicyAdmin } from './components/admin/PolicyAdmin';
import { OvertimeAdmin } from './components/admin/OvertimeAdmin';
import { ApiKeysAdmin } from './components/admin/ApiKeysAdmin';
import { WorksitesAdmin } from './components/admin/WorksitesAdmin';
import { InspectionReport } from './components/admin/InspectionReport';
import { CalendarAdmin } from './components/admin/CalendarAdmin';
import { CompanyAdmin } from './components/admin/CompanyAdmin';

const LOCATION_ASKED = 'kakuat-location-asked';

// =============================================================================
// Avisos
// =============================================================================

function UpdatePrompt({ onUpdate }: { onUpdate: () => void }) {
  return (
    <div className="fixed bottom-24 left-4 right-4 z-50 flex items-center justify-between gap-3 rounded-2xl bg-brand text-on-brand px-4 py-3.5 shadow-lg">
      <span className="text-[15px]">Hay una versión nueva</span>
      <button
        onClick={onUpdate}
        className="px-4 py-2 rounded-xl bg-on-brand text-brand font-semibold text-[14px]"
      >
        Actualizar
      </button>
    </div>
  );
}

function Toast() {
  const { activeToast, hideToast } = useAppStore();
  if (!activeToast) return null;

  const style = {
    success: 'bg-brand text-on-brand',
    error: 'bg-danger text-on-danger',
    info: 'bg-info text-white',
  }[activeToast.type];

  return (
    <button
      onClick={hideToast}
      className={`fixed top-4 left-4 right-4 z-50 flex items-center justify-between gap-3 rounded-2xl px-4 py-3.5 shadow-lg text-left ${style}`}
    >
      <span className="text-[15px]">{activeToast.message}</span>
      <span className="opacity-70">✕</span>
    </button>
  );
}

// =============================================================================
// Shell
// =============================================================================

const TABS: { path: string; icon: IconName; label: string }[] = [
  { path: '/', icon: 'clock', label: 'Fichar' },
  { path: '/planning', icon: 'calendar', label: 'Jornada' },
  { path: '/vacations', icon: 'palm', label: 'Vacaciones' },
  { path: '/documents', icon: 'file', label: 'Docs' },
];

/**
 * Salir de sesión, para el trabajador. El único `logout()` de toda la app
 * estaba en el panel del admin: en un móvil compartido de obra u hostelería
 * —o desde el 06/09 para quien entra por /recuperar-acceso sin correo— el
 * empleado no tenía salida limpia, sólo caducidad del token o borrar los
 * datos del navegador (que destruye la cola). El logout del store es el
 * seguro: conserva lo no subido en el dispositivo (ronda 3, r2b18).
 */
function LogoutButton() {
  const logout = useAppStore((s) => s.logout);
  const pendientes = useAppStore((s) => s.syncStatus.pendingCount);
  const navigate = useNavigate();

  return (
    <button
      onClick={() => {
        const sinSubir = pendientes;
        logout();
        if (sinSubir > 0) {
          // El cierre conserva la cola, pero decirlo: quien se va con fichajes
          // dentro merece saber dónde quedan y cuándo subirán.
          useAppStore.getState().showToast(
            `${sinSubir} fichaje(s) sin subir quedan en este dispositivo: subirán cuando vuelvas a entrar.`,
            'info',
          );
        }
        navigate('/login');
      }}
      className="text-[12.5px] font-semibold text-ink-3 hover:text-ink px-2 py-1 transition-colors"
    >
      Salir
    </button>
  );
}

const ETIQUETAS_EVENTO: Record<string, string> = {
  PUNCH_IN: 'Entrada',
  PUNCH_OUT: 'Salida',
  BREAK_START: 'Inicio de pausa',
  BREAK_END: 'Fin de pausa',
};

/**
 * La superficie del dead-letter: los fichajes que agotaron los reintentos y
 * dejaron de subir. Antes no la había ninguna — `errorCount` no se pintaba en
 * ninguna pantalla y el evento venenoso se reintentaba cada 30 s para siempre
 * en silencio (ronda 3, r2b18). Desde aquí se ve el error de cada uno y se
 * pueden reintentar a mano.
 */
function ColaDescartada() {
  const user = useAppStore((s) => s.user);
  const descartados = useAppStore((s) => s.syncStatus.discardedCount);
  const [detalles, setDetalles] = useState<PunchEvent[] | null>(null);

  if (descartados === 0) return null;

  const reintentar = () => {
    if (!user) return;
    setDetalles(null);
    void syncService.reintentarDescartados(user.id);
  };

  const ver = () => {
    if (!user) return;
    if (detalles) {
      setDetalles(null);
      return;
    }
    void getDiscardedEvents(user.id).then(setDetalles);
  };

  return (
    <div className="mx-4 mt-2 rounded-2xl bg-danger-soft border border-line px-4 py-3">
      <div className="flex items-center gap-3">
        <Icon name="alert" size={20} strokeWidth={1.8} className="text-danger shrink-0" />
        <p className="flex-1 text-[14px] text-danger leading-snug">
          {descartados} fichaje(s) sin subir: agotaron los reintentos y quedan apartados.
        </p>
        <button onClick={ver} className="text-[13px] font-semibold text-ink-2 underline shrink-0">
          {detalles ? 'Ocultar' : 'Ver'}
        </button>
        <button onClick={reintentar} className="text-[13px] font-semibold text-danger underline shrink-0">
          Reintentar
        </button>
      </div>
      {detalles && (
        <div className="mt-2.5 space-y-1.5 border-t border-line pt-2.5">
          {detalles.length === 0 ? (
            // El contador de la cola cuenta todo el dispositivo; las acciones
            // son del usuario con la sesión abierta. Si no hay nada suyo, se
            // dice — no un banner mudo sobre datos que no se pueden ver.
            <p className="text-[12.5px] text-ink-2 leading-snug">
              Ninguno es tuyo: son de otra persona que usa este dispositivo. Se
              muestran y se reintentan con la sesión de esa persona.
            </p>
          ) : (
            detalles.map((e) => (
              <p key={e.clientEventId} className="text-[12.5px] text-ink-2 leading-snug">
                {ETIQUETAS_EVENTO[e.eventType] ?? e.eventType} del{' '}
                {e.timestamp.toLocaleDateString()} — {e.syncError ?? 'error desconocido'}
              </p>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function BottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAppStore();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MANAGER';
  const tabs = isAdmin
    ? [...TABS, { path: '/admin/workers', icon: 'shield' as IconName, label: 'Panel' }]
    : TABS;

  return (
    <nav
      className="border-t border-line bg-canvas/90 backdrop-blur safe-area-bottom grid"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      {tabs.map((tab) => {
        const active = location.pathname === tab.path;
        return (
          <button
            key={tab.path}
            onClick={() => navigate(tab.path)}
            aria-current={active ? 'page' : undefined}
            className={`flex flex-col items-center gap-1 pt-3 pb-3.5 transition-colors ${
              active ? 'text-brand' : 'text-ink-3'
            }`}
          >
            <Icon name={tab.icon} size={24} strokeWidth={1.8} />
            <span className="text-[11px] font-semibold">{tab.label}</span>
          </button>
        );
      })}
    </nav>
  );
}

function Shell({ children, title }: { children: React.ReactNode; title?: string }) {
  const { user } = useAppStore();

  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      {title && (
        <header className="flex items-center justify-between gap-3 border-b border-line bg-surface px-4 py-3.5 safe-area-top">
          <div className="min-w-0">
            <h1 className="font-display font-extrabold text-[20px] tracking-tight truncate">{title}</h1>
            {user && (
              <p className="text-[13px] text-ink-2 truncate">
                {user.firstName} {user.lastName}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            <LogoutButton />
            <ThemeToggle />
          </div>
        </header>
      )}
      <main className="flex-1 flex flex-col overflow-y-auto">
        <div className="px-4 pt-3 empty:hidden"><TrialBanner /></div>
        <ColaDescartada />
        {children}
      </main>
      <BottomNav />
    </div>
  );
}

/** El botón de fichar es el héroe: sin cabecera, solo tema y salida en una esquina. */
function PunchRoute() {
  return (
    <div className="min-h-screen flex flex-col bg-canvas">
      <div className="flex items-center justify-end gap-1.5 px-4 pt-3 safe-area-top">
        <LogoutButton />
        <ThemeToggle />
      </div>
      <ColaDescartada />
      <main className="flex-1 flex flex-col">
        <PunchScreen />
      </main>
      <BottomNav />
    </div>
  );
}

// =============================================================================
// Guardas
// =============================================================================

function Spinner() {
  return (
    <div className="min-h-screen grid place-items-center bg-canvas">
      <div className="w-8 h-8 rounded-full border-4 border-brand border-t-transparent animate-spin" />
    </div>
  );
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAppStore();
  const [asked, setAsked] = useState(() => localStorage.getItem(LOCATION_ASKED) === '1');

  if (isLoading) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  if (!asked) {
    const done = () => {
      localStorage.setItem(LOCATION_ASKED, '1');
      setAsked(true);
    };
    return <LocationPermission onAllow={done} onSkip={done} />;
  }

  return <>{children}</>;
}

function ProtectedAdminRoute({
  children,
  requiredRoles = ['ADMIN', 'MANAGER'],
}: {
  children: React.ReactNode;
  requiredRoles?: ('ADMIN' | 'MANAGER')[];
}) {
  const { isAuthenticated, isLoading, user } = useAppStore();

  if (isLoading) return <Spinner />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!user || !requiredRoles.includes(user.role as 'ADMIN' | 'MANAGER')) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// =============================================================================
// App
// =============================================================================

export default function App() {
  const [needsUpdate, setNeedsUpdate] = useState(false);
  const [updateSW, setUpdateSW] = useState<(() => void) | null>(null);
  const { setUser, isAuthenticated } = useAppStore();

  useEffect(() => {
    const fn = registerSW({
      onNeedRefresh: () => setNeedsUpdate(true),
      onOfflineReady: () => console.log('[PWA] Listo sin conexión'),
    });
    setUpdateSW(() => fn);
  }, []);

  useEffect(() => {
    const check = async () => {
      if (!getStoredTokens()) {
        setUser(null);
        return;
      }
      try {
        const user = await getCurrentUser();
        setUser({
          id: user.id,
          email: user.email,
          firstName: user.first_name,
          lastName: user.last_name,
          role: user.role as 'ADMIN' | 'MANAGER' | 'EMPLOYEE',
          tenantId: user.tenant_id,
        });
        syncService.start(user.id);
      } catch (error) {
        // Un fallo de RED no es un 401. Tratarlos igual convertía el arranque
        // sin cobertura en un muro de login —y el login necesita servidor—,
        // justo el caso para el que existe una PWA offline-first: el móvil pasa
        // horas en un sótano, el sistema descarta la pestaña, y al reabrirla el
        // trabajador no puede fichar. Con sesión guardada y sin respuesta del
        // servidor se sigue adelante con lo que ya se sabía de él.
        const sinRespuesta =
          !navigator.onLine ||
          (error as { response?: unknown; code?: string })?.response === undefined;
        const guardado = useAppStore.getState().user;

        if (sinRespuesta && guardado) {
          // setUser es el ÚNICO que apaga `isLoading`; sin él, el arranque
          // sin cobertura con sesión guardada quedaba en Spinner eterno —
          // justo el caso insignia de una PWA offline-first (ronda 3,
          // r2b18): el trabajador no podía fichar y no veía ningún error.
          setUser(guardado);
          syncService.start(guardado.id);
        } else {
          setUser(null);
        }
      }
    };
    check();
  }, [setUser]);

  useEffect(() => {
    if (isAuthenticated) {
      useAppStore.getState().refreshWorkStateFromDB();
      // El histórico local miente tras un logout, un dispositivo nuevo o una
      // jornada abierta en el QUIOSCO (ronda 3, r2b18[8]): con red, el
      // estado del servidor es la fuente de verdad para la pantalla. Si la
      // consulta falla, la acción se queda con el estado local — el arranque
      // offline no cambia.
      void useAppStore.getState().reconcileWorkStateWithServer();
    }
  }, [isAuthenticated]);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/kiosk" element={<KioskPage />} />

          {/* Self-service: alta, verificación y recuperación (públicas) */}
          <Route path="/registro" element={<RegistroPage />} />
          <Route path="/revisa" element={<RevisaPage />} />
          <Route path="/verificar" element={<VerificarPage />} />
          <Route path="/recuperar" element={<RecuperarPage />} />
          <Route path="/restablecer" element={<RestablecerPage />} />
          <Route path="/invitacion" element={<RestablecerPage invite />} />
          <Route path="/recuperar-acceso" element={<RecuperarAccesoPage />} />
          <Route
            path="/bienvenida"
            element={
              <ProtectedAdminRoute requiredRoles={['ADMIN']}>
                <WelcomeWizard />
              </ProtectedAdminRoute>
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <PunchRoute />
              </ProtectedRoute>
            }
          />
          <Route
            path="/planning"
            element={
              <ProtectedRoute>
                <Shell title="Mi jornada">
                  <MyPlanning />
                </Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/vacations"
            element={
              <ProtectedRoute>
                <Shell title="Vacaciones">
                  <VacationsPage />
                </Shell>
              </ProtectedRoute>
            }
          />
          <Route
            path="/documents"
            element={
              <ProtectedRoute>
                <Shell title="Mis documentos">
                  <DocumentStatus />
                </Shell>
              </ProtectedRoute>
            }
          />

          {/* Back-office */}
          <Route path="/admin/inbox" element={<ProtectedAdminRoute><TimeCorrectionInbox /></ProtectedAdminRoute>} />
          <Route path="/admin/pending" element={<ProtectedAdminRoute><PendingPunchInbox /></ProtectedAdminRoute>} />
          <Route path="/admin/documents" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><DocumentValidator /></ProtectedAdminRoute>} />
          <Route path="/admin/planning" element={<ProtectedAdminRoute><ResourcePlanner /></ProtectedAdminRoute>} />
          <Route path="/admin/vacations" element={<ProtectedAdminRoute><VacationApprover /></ProtectedAdminRoute>} />
          <Route path="/admin/rotations" element={<ProtectedAdminRoute><RotationManager /></ProtectedAdminRoute>} />
          <Route path="/admin/anomalies" element={<ProtectedAdminRoute><AnomalyInbox /></ProtectedAdminRoute>} />
          <Route path="/admin/audit" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><AdminAuditLog /></ProtectedAdminRoute>} />
          <Route path="/admin/workers" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><WorkersAdmin /></ProtectedAdminRoute>} />
          <Route path="/admin/policy" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><PolicyAdmin /></ProtectedAdminRoute>} />
          <Route path="/admin/overtime" element={<ProtectedAdminRoute><OvertimeAdmin /></ProtectedAdminRoute>} />
          <Route path="/admin/api-keys" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><ApiKeysAdmin /></ProtectedAdminRoute>} />
          <Route path="/admin/worksites" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><WorksitesAdmin /></ProtectedAdminRoute>} />
          <Route path="/admin/calendar" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><CalendarAdmin /></ProtectedAdminRoute>} />
          <Route path="/admin/company" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><CompanyAdmin /></ProtectedAdminRoute>} />
          <Route path="/admin/report" element={<ProtectedAdminRoute requiredRoles={['ADMIN']}><InspectionReport /></ProtectedAdminRoute>} />

          <Route path="*" element={<NotFound />} />
        </Routes>

        <Toast />
        {needsUpdate && updateSW && (
          <UpdatePrompt
            onUpdate={() => {
              updateSW();
              setNeedsUpdate(false);
            }}
          />
        )}
      </BrowserRouter>
    </ErrorBoundary>
  );
}
